import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import UserMenu from './UserMenu.jsx';
import { fetchAllEvents, filterYouTubeEvents, filterTwitchEvents, filterKickEvents, filterEPGEvents, SEARCH_ERRORS } from '../api/events';
import {
    resolveSearchEntity,
    shouldShowResultsGallery,
    shouldTreatAsNoResults,
} from '../utils/searchEntity';
import { trackSearch } from '../api/analytics';
import ResultsGallery from './ResultsGallery.jsx';
import PageLoader from './PageLoader.jsx';
import SearchWaitingState from './SearchWaitingState.jsx';
import SearchFailedState from './SearchFailedState.jsx';
import GuestQuotaSheet from './GuestQuotaSheet.jsx';
import TrendingSearches from './TrendingSearches.jsx';
import HomeDiscovery from './homeDiscovery/HomeDiscovery.jsx';
import { getTrendingSearches } from '../api/trending';
import '../styles/mainPage.css';
import '../styles/searchStates.css';
import { useAuth } from './AuthContext.jsx';
import { supabase } from '../api/supabase';
import {
    consumePendingSearch,
    goToLogin,
    goToSignup,
    rememberSearchQuery,
} from '../utils/guestSearch';
import LegalFooter from './LegalFooter.jsx';

const SearchPrompt = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [isResultsPage, setIsResultsPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [errorType, setErrorType] = useState('NO_RESULTS');
    const [queryError, setQueryError] = useState('');
    const [viewer, setViewer] = useState(null);
    const [capabilities, setCapabilities] = useState(null);
    const [retryAfter, setRetryAfter] = useState(null);
    const [entityId, setEntityId] = useState(null);
    const [entityName, setEntityName] = useState(null);
    const [headerGlass, setHeaderGlass] = useState(false);
    const videoRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isLoggedIn, loading: authLoading, isAuthBusy } = useAuth();

    // Fading placeholder cycle: rotates suggestions while the input is idle/empty.
    const placeholderSuggestions = [
        'Search teams, sports, events, creators...',
        'Try "Champions League"...',
        'Try "Formula 1"...',
        'Try "FC Porto"...'
    ];
    const [placeholder, setPlaceholder] = useState(placeholderSuggestions[0]);
    const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
    const [searchFocused, setSearchFocused] = useState(false);

    // Trending searches (shown beneath the bar on focus)
    const [trendingItems, setTrendingItems] = useState([]);
    const [trendingOpen, setTrendingOpen] = useState(false);

    // Elapsed time drives the waiting-state message progression
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        if (!loading) {
            setElapsedTime(0);
            return;
        }
        const start = Date.now();
        const id = setInterval(() => {
            setElapsedTime(Math.floor((Date.now() - start) / 1000));
        }, 1000);
        return () => clearInterval(id);
    }, [loading]);

    useEffect(() => {
        let active = true;
        getTrendingSearches()
            .then(items => { if (active) setTrendingItems(items || []); })
            .catch(() => { if (active) setTrendingItems([]); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (isResultsPage || loading) return;
        let idx = 0;
        const interval = setInterval(() => {
            const el = inputRef.current;
            if (!el || document.activeElement === el || el.value) return;
            idx = (idx + 1) % placeholderSuggestions.length;
            setPlaceholderOpacity(0);
            setTimeout(() => {
                setPlaceholder(placeholderSuggestions[idx]);
                setPlaceholderOpacity(1);
            }, 280);
        }, 3400);
        return () => clearInterval(interval);
    }, [isResultsPage, loading]);

    const handleContainerScroll = (e) => {
        setHeaderGlass(e.currentTarget.scrollTop > 60);
    };

    // On mobile the document often scrolls instead of `.container`, so also
    // drive the glass header from window scroll.
    useEffect(() => {
        if (isResultsPage) return undefined;
        const updateGlass = () => {
            const container = document.querySelector('.container.home-active');
            const containerTop = container ? container.scrollTop : 0;
            const windowTop = window.scrollY || document.documentElement.scrollTop || 0;
            setHeaderGlass(Math.max(containerTop, windowTop) > 60);
        };
        updateGlass();
        window.addEventListener('scroll', updateGlass, { passive: true });
        return () => window.removeEventListener('scroll', updateGlass);
    }, [isResultsPage]);

    useEffect(() => {
        const checkAndSyncSession = async () => {
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) {
            } else if (session && !isLoggedIn && !authLoading) {
            }
        };
        
        if (!authLoading) {
            setTimeout(checkAndSyncSession, 500);
        }
    }, [authLoading, isLoggedIn]);

    // Handle OAuth redirects that might land on root page
    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const provider = searchParams.get('provider');

        // If we have OAuth parameters on the root page, redirect to OAuth callback handler
        if (code || state || error || provider) {
            console.log('OAuth redirect detected on root page, redirecting to callback handler:', {
                code: code ? 'present' : 'missing',
                state: state ? 'present' : 'missing',
                error,
                provider
            });
            
            // Build the OAuth callback URL
            const params = new URLSearchParams();
            if (provider) params.set('provider', provider);
            if (error) params.set('error', error);
            if (code) params.set('code', code);
            if (state) params.set('state', state);
            
            const callbackPath = error ? '/oauth/error' : '/oauth/success';
            navigate(`${callbackPath}?${params.toString()}`, { replace: true });
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        const oauthCompleted = localStorage.getItem('oauth_completed');
        if (oauthCompleted === 'true') {
            localStorage.removeItem('oauth_completed');
            
            const refreshToken = localStorage.getItem('sb-refresh-token');
            if (refreshToken) {
                window.location.reload();
            }
        }
    }, []);

    const pendingTasteQuery = location.state?.searchQuery;
    useEffect(() => {
        if (typeof pendingTasteQuery !== 'string' || !pendingTasteQuery.trim()) return;
        navigate('/', { replace: true, state: {} });
        handleSearch(pendingTasteQuery);
        // handleSearch is recreated each render; taste navigation is a one-shot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingTasteQuery]);

    const restoredPendingRef = useRef(false);
    useEffect(() => {
        if (authLoading || isAuthBusy || restoredPendingRef.current) return;
        if (typeof pendingTasteQuery === 'string' && pendingTasteQuery.trim()) return;
        const pending = consumePendingSearch();
        if (!pending?.query) return;
        restoredPendingRef.current = true;
        handleSearch(pending.query);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isAuthBusy, pendingTasteQuery]);

    const handleSearch = async (overrideQuery) => {
        const q = typeof overrideQuery === 'string' && overrideQuery ? overrideQuery : query;

        if (!q || q.trim() === '') {
            setQueryError('Enter a team, sport, or creator to search');
            return;
        }

        rememberSearchQuery(q);
        if (q !== query) setQuery(q);
        setQueryError('');
        setTrendingOpen(false);
        setLoading(true);
        setSearchError(null);
        setErrorType('NO_RESULTS');
        setEntityId(null);
        setEntityName(null);
        setResults([]);

        try {
            const result = await fetchAllEvents(q);
            const allEvents = result.events;
            setViewer(result.viewer);
            setCapabilities(result.capabilities || null);
            setRetryAfter(result.retryAfter);

            if (result.error === SEARCH_ERRORS.QUOTA) {
                setSearchError(result.error);
                setErrorType(result.error);
                setIsResultsPage(true);
                return;
            }

            if (result.error === SEARCH_ERRORS.RATE_LIMIT
                || result.error === SEARCH_ERRORS.UNAUTHORIZED
                || result.error === SEARCH_ERRORS.UNAVAILABLE
                || result.error === SEARCH_ERRORS.TIMEOUT
                || result.error === SEARCH_ERRORS.NETWORK
                || result.error === SEARCH_ERRORS.UNKNOWN) {
                setSearchError(result.error);
                setErrorType(result.error);
                setIsResultsPage(true);
                return;
            }

            const { entityId: nextEntityId, entityName: nextEntityName } = resolveSearchEntity({
                entityId: result.entityId,
                entityName: result.entityName,
                events: allEvents,
                query: q,
            });
            setEntityId(nextEntityId);
            setEntityName(nextEntityName);

            const youtubeStreams = filterYouTubeEvents(allEvents);
            const twitchStreams = filterTwitchEvents(allEvents);
            const kickStreams = filterKickEvents(allEvents);
            const epgStreams = filterEPGEvents(allEvents);

            const combinedStreams = [
                ...youtubeStreams,
                ...twitchStreams,
                ...kickStreams,
                ...(epgStreams?.liveEvents || []),
                ...(epgStreams?.upcomingEvents || []),
            ];

            trackSearch(q, 'combined', combinedStreams.length).catch(() => {
            });

            setIsResultsPage(true);
            setResults(combinedStreams);

            if (shouldTreatAsNoResults({ entityId: nextEntityId, eventCount: combinedStreams.length })) {
                setSearchError('NO_RESULTS');
                setErrorType('NO_RESULTS');
            }
        } catch (error) {
            let nextError = SEARCH_ERRORS.UNKNOWN;
            if (error.message?.includes('timeout') || error.message?.includes('TIMEOUT')) {
                nextError = SEARCH_ERRORS.TIMEOUT;
            } else if (error.message?.includes('network') || error.message?.includes('Network')) {
                nextError = SEARCH_ERRORS.NETWORK;
            }

            setSearchError(nextError);
            setErrorType(nextError);
            setIsResultsPage(true);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setIsResultsPage(false);
        setQuery('');
        setResults([]);
        setSearchError(null);
        setErrorType('NO_RESULTS');
        setQueryError('');
        setViewer(null);
        setCapabilities(null);
        setRetryAfter(null);
        setEntityId(null);
        setEntityName(null);
        if (videoRef.current) videoRef.current.pause();
    };

    const handleRetry = () => {
        setSearchError(null);
        setErrorType('NO_RESULTS');
        handleSearch();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        } else if (e.key === 'Escape') {
            setTrendingOpen(false);
        }
    };

    const handleTrendingSelect = (selected) => {
        setTrendingOpen(false);
        handleSearch(selected);
    };

    const isQuotaSheet = searchError === SEARCH_ERRORS.QUOTA;
    const keepQuotaResults = isQuotaSheet && results.length > 0;
    const showGallery = shouldShowResultsGallery({
        searchError,
        keepQuotaResults,
        entityId,
        resultsLength: results.length,
    });

    if (isAuthBusy) {
        return <PageLoader />;
    }

    return (
        <div
            className={`container${!isResultsPage ? ' home-active' : ' results-active'}${loading && !isResultsPage ? ' search-waiting' : ''}`}
            style={{ minHeight: '100vh', position: 'relative', background: 'transparent' }}
            onScroll={handleContainerScroll}
        >
            {/* Header - Only show on search page, not on results page */}
            {!isResultsPage && (
                <header className={`app-header${headerGlass ? ' glass' : ''}`}>
                    <div className="app-header-logo">
                        <span className="logo-text-home">LiveNow</span>
                    </div>
                    <nav className="app-header-nav">
                        {isLoggedIn ? (
                            <UserMenu />
                        ) : (
                            <>
                                <button className="livenow-login-btn" onClick={() => goToLogin(navigate, query)}>Log in</button>
                                <button className="livenow-signup-btn" onClick={() => goToSignup(navigate, query)}>Sign up</button>
                            </>
                        )}
                    </nav>
                </header>
            )}

            {/* Main Content */}
            <div className={isResultsPage ? 'results-shell' : 'home-shell'}>
                {!isResultsPage && !loading && (
                    <section className="hero">
                        <h1 className="livenow-title">Go Live, <span className="livenow-title-grad">Stay Connected</span></h1>
                        <p className="livenow-subtitle">See what's happening live - anywhere, anytime</p>
                        <div className="livenow-search-bar">
                            <div className="livenow-search-field">
                                {!query && !searchFocused && (
                                    <span
                                        className="livenow-search-suggestion"
                                        style={{ opacity: placeholderOpacity, transition: 'opacity 0.28s ease' }}
                                        aria-hidden="true"
                                    >
                                        {placeholder}
                                    </span>
                                )}
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={e => {
                                        setQuery(e.target.value);
                                        if (queryError) setQueryError('');
                                    }}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => {
                                        setSearchFocused(true);
                                        setTrendingOpen(true);
                                    }}
                                    onBlur={() => {
                                        setSearchFocused(false);
                                        setTimeout(() => setTrendingOpen(false), 150);
                                    }}
                                    placeholder=""
                                    className="livenow-search-input"
                                    autoFocus
                                />
                            </div>
                            <button className="livenow-search-btn" onClick={() => handleSearch()} aria-label="Search">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                            {trendingOpen && (
                                <TrendingSearches items={trendingItems} onSelect={handleTrendingSelect} />
                            )}
                        </div>
                        {queryError ? (
                            <p className="livenow-search-error" role="status">{queryError}</p>
                        ) : null}

                        <button
                            type="button"
                            className={`scroll-hint${headerGlass ? ' is-hidden' : ''}`}
                            onClick={() => {
                                const target = document.getElementById('whats-on');
                                const container = document.querySelector('.container.home-active');
                                if (target && container) {
                                    const top = target.getBoundingClientRect().top
                                        - container.getBoundingClientRect().top
                                        + container.scrollTop
                                        - 72;
                                    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
                                    return;
                                }
                                target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            aria-label="Browse what's on"
                        >
                            WHAT&apos;S ON
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                    </section>
                )}

                {!isResultsPage && !loading && (
                    <HomeDiscovery onSearch={handleSearch} trending={trendingItems} />
                )}
                {loading && !isResultsPage && (
                    <SearchWaitingState query={query} elapsedTime={elapsedTime} />
                )}
                {isResultsPage && !loading && (
                    isQuotaSheet && !keepQuotaResults ? (
                        <GuestQuotaSheet
                            query={query}
                            onBack={handleBack}
                        />
                    ) : showGallery ? (
                        <ResultsGallery
                            results={results}
                            query={entityName || query}
                            entityId={entityId}
                            onBack={handleBack}
                            videoRef={videoRef}
                            viewer={viewer}
                            capabilities={capabilities}
                            quotaNotice={keepQuotaResults}
                        />
                    ) : (
                        <SearchFailedState 
                            query={query}
                            errorType={errorType}
                            retryAfter={retryAfter}
                            onRetry={handleRetry}
                            onBack={handleBack}
                        />
                    )
                )}
            </div>

            {loading && !isResultsPage ? null : <LegalFooter />}
        </div>
    );
};

export default SearchPrompt;